import { useRef, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import type { WaterUndoEntry } from '../types/waterUndo';

interface UseWaterUndoReturn {
  /** Record a water action for potential undo */
  recordWater: (
    plantId: string,
    previousLastWatered: Timestamp | null,
    toastId: string
  ) => void;

  /** Get the previous lastWatered value for undo, returns undefined if not found */
  getUndoValue: (plantId: string) => Timestamp | null | undefined;

  /** Clear undo entry after use or expiration */
  clearUndo: (plantId: string) => void;

  /** Check if undo is available for a plant */
  canUndo: (plantId: string) => boolean;
}

/**
 * Hook for managing water action undo state.
 * Stores undo entries in a ref (session-only, not persisted).
 * One entry per plant - latest water action wins.
 */
export function useWaterUndo(): UseWaterUndoReturn {
  const undoMapRef = useRef<Map<string, WaterUndoEntry>>(new Map());

  const recordWater = useCallback(
    (plantId: string, previousLastWatered: Timestamp | null, toastId: string) => {
      const entry: WaterUndoEntry = {
        plantId,
        previousLastWatered,
        toastId,
        timestamp: Date.now(),
      };
      undoMapRef.current.set(plantId, entry);
    },
    []
  );

  const getUndoValue = useCallback((plantId: string): Timestamp | null | undefined => {
    const entry = undoMapRef.current.get(plantId);
    if (!entry) {
      return undefined;
    }
    return entry.previousLastWatered;
  }, []);

  const clearUndo = useCallback((plantId: string) => {
    undoMapRef.current.delete(plantId);
  }, []);

  const canUndo = useCallback((plantId: string): boolean => {
    return undoMapRef.current.has(plantId);
  }, []);

  return {
    recordWater,
    getUndoValue,
    clearUndo,
    canUndo,
  };
}
